import {
  DEPOSIT_BOOKING_CONTEXTS,
  DepositAmountType,
  DepositRequirementSource,
  type DepositBookingContext,
  type IDepositAmount,
  type IDepositSettings,
  type IDepositTreatmentOverride,
} from '@principle-theorem/principle-core/interfaces';
import { DAYS_OF_WEEK, type DayOfWeek } from '@principle-theorem/shared';

export interface IDepositRequirement {
  required: boolean;
  amount?: IDepositAmount;
  source: DepositRequirementSource;
}

export interface IShouldRequireDepositOptions {
  settings?: IDepositSettings;
  treatmentOverride?: IDepositTreatmentOverride;
  bookingContext: DepositBookingContext;
  appointmentDay?: DayOfWeek;
  patientDepositRequired?: boolean;
}

/** Owns the precedence and applicability rules for requiring a deposit. */
export class DepositPolicy {
  /**
   * Resolves applicability centrally so blanket and treatment-specific context
   * rules cannot diverge between booking paths.
   */
  static resolveAppliesTo(
    settings?: IDepositSettings,
    treatmentOverride?: IDepositTreatmentOverride
  ): DepositBookingContext[] {
    const overrideAppliesTo =
      treatmentOverride?.depositRequired === true
        ? treatmentOverride.appliesTo
        : undefined;
    if (overrideAppliesTo?.length) {
      return overrideAppliesTo;
    }
    return settings?.appliesTo?.length
      ? settings.appliesTo
      : DEPOSIT_BOOKING_CONTEXTS;
  }

  /**
   * Applies the master switch, patient override, template rule, context, and day
   * precedence in one place so every booking surface reaches the same decision.
   */
  static shouldRequire({
    settings,
    treatmentOverride,
    bookingContext,
    appointmentDay,
    patientDepositRequired,
  }: IShouldRequireDepositOptions): IDepositRequirement {
    if (!settings?.enabled) {
      return { required: false, source: DepositRequirementSource.None };
    }

    if (patientDepositRequired === true) {
      return {
        required: true,
        amount: DepositPolicy._resolvePatientDepositAmount(
          settings,
          treatmentOverride
        ),
        source: DepositRequirementSource.Patient,
      };
    }

    if (treatmentOverride?.depositRequired !== true) {
      return {
        required: false,
        source:
          treatmentOverride === undefined
            ? DepositRequirementSource.None
            : DepositRequirementSource.TreatmentTemplate,
      };
    }

    const appliesTo = DepositPolicy.resolveAppliesTo(
      settings,
      treatmentOverride
    );
    if (!appliesTo.includes(bookingContext)) {
      return {
        required: false,
        source: DepositRequirementSource.BookingContextExcluded,
      };
    }

    const depositRequiredDays =
      treatmentOverride.depositRequiredDays ?? DAYS_OF_WEEK;
    if (appointmentDay && !depositRequiredDays.includes(appointmentDay)) {
      return {
        required: false,
        source: DepositRequirementSource.AppointmentDayExcluded,
      };
    }

    return {
      required: true,
      amount: treatmentOverride.amount,
      source: DepositRequirementSource.TreatmentTemplate,
    };
  }

  /**
   * Uses the treatment amount for a flagged patient when required, then the
   * brand's patient amount; an unconfigured amount resolves as undefined.
   */
  private static _resolvePatientDepositAmount(
    settings: IDepositSettings,
    treatmentOverride?: IDepositTreatmentOverride
  ): IDepositAmount | undefined {
    if (treatmentOverride?.depositRequired === true) {
      return treatmentOverride.amount;
    }
    if (settings.patientDepositAmountCents) {
      return {
        type: DepositAmountType.Fixed,
        amountCents: settings.patientDepositAmountCents,
      };
    }
    return;
  }
}
